import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import {
  Area,
  AreaChart as ReAreaChart,
  LabelFormatter,
  Legend,
  LegendProps,
  ResponsiveContainer,
  TickFormatterFunction,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import getChartColor from '../chart/getChartColor';
import { getRechartsAxisDomains } from '../chart';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    root: {
      '& ul': {
        height: '100%',
        overflowY: 'auto',
      },
    },
  })
);

type YKey<T> = {
  key: keyof T;
  color?: string;
};

interface Props<T> {
  height?: number | string;
  width?: number | string;
  data?: T[];
  x?: {
    key?: keyof T;
    formatter?: TickFormatterFunction;
    label?: string | number;
    name?: string | number;
  };
  y?: {
    keys: YKey<T>[];
    formatter?: TickFormatterFunction;
    label?: string | number;
    name?: string | number;
  };
  tooltip?: {
    formatter?: LabelFormatter;
  };
  animate?: boolean;
  legend?: LegendProps;
}

export default function AreaChart<DataInstance>({
  data = [],
  width = '100%',
  height = '100%',
  x,
  y,
  tooltip,
  animate = false,
  legend,
}: Props<DataInstance>) {
  const classes = useStyles();
  return (
    <ResponsiveContainer width={width} height={height} className={classes.root}>
      <ReAreaChart data={(data as unknown) as readonly object[]}>
        <XAxis
          dataKey={x?.key as string | undefined}
          domain={['dataMin', 'dataMax']}
          tickFormatter={x?.formatter}
          type="number"
          label={x?.label}
          name={x?.name}
        />
        <YAxis
          domain={getRechartsAxisDomains<DataInstance>(data, y?.keys)}
          label={y?.label}
          tickFormatter={y?.formatter}
          name={y?.name}
        />
        {(y?.keys || []).map((yKey, i) => {
          const { key, color = getChartColor(i) } = yKey;
          return (
            <Area
              type="monotone"
              key={key as string}
              dataKey={key as string}
              stroke={color}
              fill={color}
              fillOpacity={1}
              isAnimationActive={animate}
              stackId={(x?.key || 1) as number}
            />
          );
        })}
        <Tooltip
          cursor={{ fill: 'rgb(0,0,0,0)' }}
          labelFormatter={tooltip?.formatter}
          animationDuration={0}
        />
        {legend && <Legend {...legend} />}
      </ReAreaChart>
    </ResponsiveContainer>
  );
}
